import type { components } from '@/api/schema'
import { pageTotal } from '@/features/market/api/marketMappers'
import { newsletterApi } from '@/features/newsletter/api/newsletterApi'
import {
  newsletterArchivePageSchema,
  newsletterCardSchema,
  newsletterIssueSchema,
  newsletterSectionSchema,
  type NewsletterArchivePage,
  type NewsletterCard,
  type NewsletterIssue,
  type NewsletterSection,
} from '@/schemas/newsletter'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * Wire → domain for MoneyMitra Weekly.
 *
 * Structured like `portfolioService.ts`, for the same two reasons. Wire types
 * come from `@/api/schema` and are never restated, so a regenerated
 * `schema.d.ts` that renames a field breaks the build HERE, which is where it
 * should break; and every mapper's result leaves through its Zod schema, so
 * contract drift fails at the call rather than inside a renderer.
 *
 * ── Three things about this API that silently corrupt behaviour ──────────────
 *
 * 1. `meta.count` IS NOT THE TOTAL. `api/v5/envelope.py` sets
 *    `count = len(rows)` — the rows in THIS response — and then merges the
 *    service's `PageMeta`, which is where `total` (and `page`, `page_size`,
 *    `total_pages`) actually comes from. So `page * page_size < count` is not
 *    a paging test: on a full page it is `20 < 20`, false, and the archive
 *    claims one page forever. `pageTotal` reads `total ?? count` in that
 *    order, which is why it is reused here rather than the arithmetic
 *    being inlined.
 *
 * 2. NULL IS NOT ZERO, and the mappers below are deliberately dumb about it:
 *    a nullable decimal is passed through untouched, never `?? '0'` and never
 *    coalesced in a mapper "for convenience". `sections.py` already decided
 *    that a missing, blank or unparseable number is `None`; flattening that to
 *    0 here would draw a chart floor and a `0.00` table cell that look like
 *    recorded facts.
 *
 * 3. `sections` CAN BE EMPTY. An issue authored but not filled in serialises
 *    as `[]`, and `latest` will happily return it — so it is a normal reader
 *    state to design for, not an error to guard against.
 */

const toMetric = (wire: Wire<'NewsletterMetric'>) => ({
  label: wire.label,
  // Verbatim. It was formatted at publish time and may not be a decimal at all
  // ("Rs 8.2 Arba"), so there is nothing here to parse or re-render.
  value: wire.value,
})

const toSeriesPoint = (wire: Wire<'NewsletterSeriesPoint'>) => ({
  label: wire.label,
  value: wire.value,
})

const toMarketEvent = (wire: Wire<'NewsletterMarketEvent'>) => ({
  headline: wire.headline,
  detail: wire.detail,
})

const toSectorRow = (wire: Wire<'NewsletterSectorRow'>) => ({
  sector: wire.sector,
  open: wire.open,
  close: wire.close,
  change: wire.change,
})

/**
 * The union mapper.
 *
 * A `switch` on the wire's own discriminator, which the generated types make a
 * real literal (`type: "index_summary"`, not `string`) because the spec
 * carries a `discriminator.mapping`. TypeScript therefore checks this
 * exhaustively: adding a fifth kind to `schema.d.ts` breaks this function
 * before it can reach a renderer.
 *
 * The parse at the end is what turns the wire's bare decimal strings into
 * branded `Money`, so the brand is applied at exactly one place per value and
 * a mapper cannot hand a raw string to a formatter.
 */
export const mapSection = (wire: Wire<'NewsletterSection'>): NewsletterSection => {
  switch (wire.type) {
    case 'index_summary':
      return newsletterSectionSchema.parse({
        type: 'index_summary',
        title: wire.title,
        metrics: wire.metrics.map(toMetric),
        series: wire.series.map(toSeriesPoint),
      })
    case 'market_events':
      return newsletterSectionSchema.parse({
        type: 'market_events',
        title: wire.title,
        events: wire.events.map(toMarketEvent),
        // `null`, not omitted: the schema requires the key, and "no essay this
        // week" is a fact the reader renders (as nothing), not a missing field.
        essay: wire.essay,
      })
    case 'did_you_know':
      return newsletterSectionSchema.parse({
        type: 'did_you_know',
        title: wire.title,
        body: wire.body,
      })
    case 'sector_table':
      return newsletterSectionSchema.parse({
        type: 'sector_table',
        title: wire.title,
        rows: wire.rows.map(toSectorRow),
      })
  }
}

/** The archive card. Parsed, so a drifted field fails here and names itself. */
export const mapCard = (wire: Wire<'NewsletterCard'>): NewsletterCard =>
  newsletterCardSchema.parse({
    issueNumber: wire.issue_number,
    slug: wire.slug,
    source: wire.source,
    headline: wire.headline,
    summary: wire.summary,
    coverImage: wire.cover_image,
    readTimeMinutes: wire.read_time_minutes,
    publishedAt: wire.published_at,
  })

/** The reader page: the card's fields plus the ordered blocks. */
export const mapIssue = (wire: Wire<'NewsletterDetail'>): NewsletterIssue =>
  newsletterIssueSchema.parse({
    issueNumber: wire.issue_number,
    slug: wire.slug,
    source: wire.source,
    headline: wire.headline,
    summary: wire.summary,
    coverImage: wire.cover_image,
    readTimeMinutes: wire.read_time_minutes,
    publishedAt: wire.published_at,
    sections: wire.sections.map(mapSection),
  })

/** The archive's default page size. Well under mm-be's ceiling of 200. */
export const NEWSLETTER_PAGE_SIZE = 12

export const newsletterService = {
  /**
   * One page of the archive.
   *
   * Offset paging, not the cursor paging the News feed uses. The archive is a
   * numbered back catalogue — "Issue 20 … Issue 1" — that grows by one item a
   * week, so the re-served-story problem a time-ordered feed has does not
   * arise, and a member who wants issue 4 wants to jump to a page, not scroll.
   */
  archive: async (page = 1, pageSize = NEWSLETTER_PAGE_SIZE): Promise<NewsletterArchivePage> => {
    const result = await newsletterApi.archive({ page, page_size: pageSize })
    // `result.rows.length` is the fallback, so a meta that carried neither
    // `total` nor `count` degrades to "this page is everything" rather than
    // to an infinite pager.
    const total = pageTotal(result.meta, result.rows.length)
    return newsletterArchivePageSchema.parse({
      rows: result.rows.map(mapCard),
      page,
      pageSize,
      total,
    })
  },

  /** The most recent published issue. 404s when nothing is published at all. */
  latest: async (): Promise<NewsletterIssue> => mapIssue(await newsletterApi.latest()),

  /** One issue by its number — the route key, never the slug. */
  issue: async (issueNumber: number): Promise<NewsletterIssue> =>
    mapIssue(await newsletterApi.issue(issueNumber)),
}
